import type { PendoProfile } from "./profile/pendo-profile.model";

/**
 * A profile as Pendo's agent is sent it.
 *
 * The property keys and representations are what existing Pendo segments and
 * reports are built on — add keys freely, but don't rename or retype the ones
 * already sent.
 *
 * Pendo merges what's sent into the visitor and account it has stored: a
 * property sent overwrites the stored value, and one left out keeps it. So a
 * payload leaves out whatever the profile doesn't know, rather than
 * overwriting what Pendo learned elsewhere.
 * https://support.pendo.io/hc/en-us/community/posts/360077884291-Overwriting-Metadata
 */
export type PendoPayload = Record<
    "visitor" | "account",
    Record<string, unknown>
>;

export namespace PendoPayload {
    export function from({ visitor, account }: PendoProfile): PendoPayload {
        return {
            visitor: definedOnly({
                id: visitor.id.toLowerCase(),
                email: visitor.email,
                firstName: visitor.firstName,
                lastName: visitor.lastName,
                // Pendo's own key, hence snake_case among our camelCase fields:
                // https://support.pendo.io/hc/en-us/articles/360031862272-Install-Pendo-on-a-single-page-web-application
                full_name: fullName(visitor),
                createdDate: legacyString(visitor.createdAt),
                lastLogin: legacyString(visitor.lastLoginAt),
                isInternal: legacyString(visitor.internal),
                ...authorityFlags(visitor.authorities),
            }),
            account: definedOnly({
                id: account.id.toLowerCase(),
                name: account.name,
                subdomain: account.subdomain,
                creationDate: legacyString(account.createdAt),
                firmPlanTier: account.planTier,
                isBeta: legacyString(account.beta),
                isDemo: account.demo,
                cancellationDate: legacyString(account.cancelledAt),
                billingEnabled: account.modules?.billing,
                timeTrackingEnabled: account.modules?.timeTracking,
                leadsEnabled: account.modules?.leads,
                multiLocationEnabled: account.modules?.multiLocation,
                clientAccessEnabled: account.modules?.clientAccess,
                clientAccessProjectsEnabled:
                    account.modules?.clientAccessProjects,
                clientAccessRequestsEnabled:
                    account.modules?.clientAccessRequests,
                clientAccessInvoicingEnabled:
                    account.modules?.clientAccessInvoicing,
                clientMessagesEnabled: account.modules?.clientMessages,
                stripeConnected: account.modules?.stripeConnected,
                qboPaymentFlowMode: account.modules?.qboPaymentFlowMode,
                adobeSignEnabled: account.modules?.adobeSign,
                rightSignatureEnabled: account.modules?.rightSignature,
                documentExportsEnabled: account.modules?.documentExports,
            }),
        };
    }
}

/**
 * One flag per authority a firm user can hold, which Pendo segments on more
 * readily than a list. An authority missing here isn't sent at all.
 */
const AUTHORITY_KEYS: Readonly<Record<string, string>> = {
    ROLE_ACCESS_ALL_RESTRICTED_DOCS: "hasAccessAllRestrictedDocsAuthority",
    ROLE_ADMIN: "hasAdminAuthority",
    ROLE_ADOBE_ESIGN: "hasAdobeEsignAuthority",
    ROLE_BILLING: "hasBillingAuthority",
    ROLE_CLIENT_ACCESS: "hasClientAccessAuthority",
    ROLE_CLIENT_ACCESS_RESTRICTED: "hasClientAccessRestrictedAuthority",
    ROLE_CLIENT_MESSAGES: "hasClientMessagesAuthority",
    ROLE_CLIENT_PORTAL_IMPERSONATION: "hasClientPortalImpersonationAuthority",
    ROLE_DOCUMENT_EXPORT: "hasDocumentExportAuthority",
    ROLE_LIMITED_ACCESS: "hasLimitedAccessAuthority",
    ROLE_MANAGE_CLIENT_PORTAL_ACCESS: "hasManageClientPortalAccessAuthority",
    ROLE_PARTNER_IN_CHARGE: "hasPartnerInChargeAuthority",
    ROLE_RECURRING_PROJECTS: "hasRecurringProjectsAuthority",
    ROLE_REPORTING: "hasReportingAuthority",
    ROLE_SUPERUSER: "hasSuperuserAuthority",
    ROLE_TASK_HISTORY_READ: "hasTaskHistoryReadAuthority",
    ROLE_TIME_ADMIN: "hasTimeAdminAuthority",
    ROLE_USER: "hasUserAuthority",
};

function authorityFlags(
    authorities: readonly string[] | undefined,
): Record<string, boolean> {
    if (authorities === undefined) return {};
    return Object.fromEntries(
        Object.entries(AUTHORITY_KEYS).map(([authority, key]) => [
            key,
            authorities.includes(authority),
        ]),
    );
}

function fullName({
    firstName,
    lastName,
}: PendoProfile.Visitor): string | undefined {
    if (firstName === undefined && lastName === undefined) return undefined;
    return [firstName, lastName].filter(Boolean).join(" ") || undefined;
}

/**
 * Dates (as epoch milliseconds) and booleans were first sent as strings, and
 * Pendo's fields are typed to match.
 */
function legacyString(
    value: number | boolean | null | undefined,
): string | null | undefined {
    if (value === undefined) return undefined;
    if (value === null) return null;
    return String(value);
}

function definedOnly(values: Record<string, unknown>): Record<string, unknown> {
    return Object.fromEntries(
        Object.entries(values).filter(([, value]) => value !== undefined),
    );
}
